import { useMemo, useState } from 'react';
import { PhotoAsset, PhotoArtifactTab, Artifact } from '../types.ts/story';
import { SortBy, sortPhotoAssets } from '../utility/general';
import { useVideoCreatorStore } from '../stores-v2/VideoCreatorStoreContext';

const logoArtifactToPhotoAsset = (artifact: Artifact): PhotoAsset => {
  return {
    id: artifact.id,
    title: artifact.title,
    src: artifact.responsiveImage!.src,
    type: 'artifact',
    aspectRatio: parseFloat(artifact.width) / parseFloat(artifact.height),
    _createdAt: artifact._createdAt,
    ...artifact.customData,
  };
};

const photoToPhotoAsset = (artifact: Artifact): PhotoAsset => {
  return {
    id: artifact.id,
    title: artifact.title,
    src: artifact.responsiveImage?.src!,
    type: 'artifact',
    aspectRatio: artifact.responsiveImage?.aspectRatio,
    _createdAt: artifact._createdAt,
    ...artifact.customData,
  };
};

export const usePhotoAssetManager = () => {
  const videoCreator = useVideoCreatorStore();
  const [sortBy, setSortBy] = useState<SortBy>('-createdAt');

  // Search terms for different tabs
  const [artifactSearchTerm, setArtifactSearchTerm] = useState('');
  const [orgArtifactSearchTerm, setOrgArtifactSearchTerm] = useState('');
  const [orgLogoSearchTerm, setOrgLogoSearchTerm] = useState('');

  // Get original data from source
  const artifactsOriginal = useMemo(
    () =>
      sortPhotoAssets(
        videoCreator?.story?.storyArtifacts
          ?.filter((a) => a.responsiveImage)
          .map((a) => photoToPhotoAsset(a)),
        sortBy,
      ),
    [videoCreator?.story?.storyArtifacts, sortBy],
  );

  const organizationArtifactsOriginal = useMemo(
    () =>
      sortPhotoAssets(
        videoCreator?.story?._allReferencingShowcases?.flatMap(
          (album) =>
            album.organizationArtifacts
              ?.filter((a) => a.responsiveImage)
              .map((a) => photoToPhotoAsset(a)) || [],
        ),
        sortBy,
      ),
    [videoCreator?.story?._allReferencingShowcases, sortBy],
  );

  const organizationLogosOriginal = useMemo(
    () =>
      sortPhotoAssets(
        videoCreator?.story?._allReferencingShowcases?.flatMap(
          (album) =>
            album.organizationLogos
              ?.filter((a) => a.responsiveImage)
              .map((a) => logoArtifactToPhotoAsset(a)) || [],
        ),
        sortBy,
      ),
    [videoCreator?.story?._allReferencingShowcases, sortBy],
  );

  // Compute filtered lists based on search terms
  const storyPhotos = useMemo(() => {
    if (!artifactSearchTerm) return artifactsOriginal;
    return artifactsOriginal.filter(
      (a) =>
        a?.title?.toLowerCase()?.includes(artifactSearchTerm.toLowerCase()),
    );
  }, [artifactsOriginal, artifactSearchTerm]);

  const albumPhotos = useMemo(() => {
    if (!orgArtifactSearchTerm) return organizationArtifactsOriginal;
    return organizationArtifactsOriginal.filter(
      (a) =>
        a?.title?.toLowerCase()?.includes(orgArtifactSearchTerm.toLowerCase()),
    );
  }, [organizationArtifactsOriginal, orgArtifactSearchTerm]);

  const albumLogos = useMemo(() => {
    if (!orgLogoSearchTerm) return organizationLogosOriginal;
    return organizationLogosOriginal.filter(
      (a) => a?.title?.toLowerCase()?.includes(orgLogoSearchTerm.toLowerCase()),
    );
  }, [organizationLogosOriginal, orgLogoSearchTerm]);

  const getResourcesForTab = (tab: PhotoArtifactTab): PhotoAsset[] => {
    switch (tab) {
      case PhotoArtifactTab.story:
        return storyPhotos;
      case PhotoArtifactTab.album:
        return albumPhotos;
      case PhotoArtifactTab.logo:
        return albumLogos;
      default:
        return [];
    }
  };

  const handleSearchArtifacts = (inputString: string, tab: PhotoArtifactTab) => {
    switch (tab) {
      case PhotoArtifactTab.story:
        setArtifactSearchTerm(inputString);
        break;
      case PhotoArtifactTab.logo:
        setOrgLogoSearchTerm(inputString);
        break;
      case PhotoArtifactTab.album:
        setOrgArtifactSearchTerm(inputString);
        break;
    }
  };

  const handleSort = () => {
    const newSort = sortBy === '-createdAt' ? 'createdAt' : '-createdAt';
    setSortBy(newSort);
  };

  const refreshAssetsForTab = (tab: PhotoArtifactTab) => {
    const resources = getResourcesForTab(tab);
    const sortedResources = sortPhotoAssets(resources, sortBy);
    
    videoCreator.selectedPhotoAssets = {
      ...videoCreator.selectedPhotoAssets,
      tab,
      resource: sortedResources,
    };
  };

  return {
    // Data
    storyPhotos,
    albumPhotos,
    albumLogos,
    sortBy,
    
    // Search terms
    artifactSearchTerm,
    orgArtifactSearchTerm,
    orgLogoSearchTerm,
    
    // Actions
    handleSearchArtifacts,
    handleSort,
    getResourcesForTab,
    refreshAssetsForTab,
    
    // Utilities
    setSortBy,
  };
};